import { notification } from "antd";
import axios, { AxiosError, AxiosInstance } from "axios";
import { env } from "@shared/config.ts";
import { checkToken } from "@shared/lib/session-service";
import { tracerError, TracerIssueKey, TRACER_ERROR_USER_MESSAGES } from "@shared/lib/tracer";
import { requestAuthInterceptor } from "./auth-middleware";

const command = import.meta.env.VITE_COMMAND;
const backendUrl = import.meta.env.VITE_API_BASE_URL;

function buildIssueKey(method: string, status: number | undefined): string {
  const m = method.toUpperCase();
  if (status === undefined) {
    const map: Record<string, TracerIssueKey> = {
      GET: TracerIssueKey.HttpNetworkGet,
      POST: TracerIssueKey.HttpNetworkPost,
      PUT: TracerIssueKey.HttpNetworkPut,
      PATCH: TracerIssueKey.HttpNetworkPatch,
      DELETE: TracerIssueKey.HttpNetworkDelete,
    };
    return map[m] ?? (`http-network-${m}` as TracerIssueKey);
  }
  const map: Record<string, TracerIssueKey> = {
    GET: TracerIssueKey.HttpServerGetError,
    POST: TracerIssueKey.HttpServerPostError,
    PUT: TracerIssueKey.HttpServerPutError,
    PATCH: TracerIssueKey.HttpServerPatchError,
    DELETE: TracerIssueKey.HttpServerDeleteError,
  };
  return map[m] ?? (`http-${status}-${m}` as TracerIssueKey);
}

const addErrorInterceptor = (api: AxiosInstance) => {
  api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;
      const method = (error.config?.method ?? "unknown").toUpperCase();
      const isServerError = status !== undefined && status >= 500;
      const isNetworkError = !error.response && Boolean(error.request);

      if (isServerError || isNetworkError) {
        const issueKey = buildIssueKey(method, isNetworkError ? undefined : status);
        const userMsg = TRACER_ERROR_USER_MESSAGES[issueKey];

        if (userMsg) {
          notification.error({
            message: userMsg.title,
            description: userMsg.description,
            placement: "topRight",
            duration: 5,
          });
        }

        try {
          tracerError.error(error, {
            severity: "error",
            component: "axios",
            issueKey,
            keys: {
              url: error.config?.url ?? "unknown",
              method,
              status: status ?? 0,
            },
          });
        } catch {
          // ignore tracer failures
        }
      }

      throw error;
    },
  );
};

const createApi = (baseURL: string) => {
  const api = axios.create({
    baseURL: `${backendUrl}${baseURL}`,
  });

  api.interceptors.request.use(requestAuthInterceptor);
  addErrorInterceptor(api);

  return api;
};

const createLegacyApi = (
  baseURL: string,
  settings = { useOldToken: false },
) => {
  const prefix = command === "serve" ? "" : env.API_OLD_BASE_URL;
  const url = settings.useOldToken ? baseURL : `${prefix}${baseURL}`;

  const api = axios.create({
    baseURL: url,
  });

  api.interceptors.request.use((config) => {
    return checkToken(config, settings);
  });
  addErrorInterceptor(api);

  return api;
};

export const userServiceApi = createApi("/user-service");
export const buildsServiceApi = createApi("/builds-service/api/v1");
export const s3AdapterApi = createApi("/s3-adapter/api/v1");
export const filestore = createApi("/filestore");
export const companyServiceApi = createApi("/company-service/api/v1");
export const journalsServiceApi = createApi("/journal-service/api/v1");
export const poaServiceApi = createApi("/poa-service/api/v1");
export const oldBaseApi =
  command === "serve"
    ? createLegacyApi("/old-builddocs-api", { useOldToken: true })
    : createLegacyApi(`${env.API_OLD_BASE_URL}`, { useOldToken: true });

export const filestoreOldAuth = (() => {
  const api = axios.create({ baseURL: `${backendUrl}/filestore` });
  api.interceptors.request.use((config) => checkToken(config, { useOldToken: true }));
  addErrorInterceptor(api);
  return api;
})();

export type { ModernApi, LegacyApi } from "./project";
